import {useEffect, useRef} from 'react'
import {css} from '@emotion/react'
import {FileTextOutlined} from '@ant-design/icons'
import {useMapContext} from '@/components/MindMap/index.mjs'

const cssNodeCommentEditor = css({
    width: 'max-content',
    lineHeight: 1.2,
    whiteSpace: 'pre-wrap',
    backgroundColor: '#ffc',
    padding: 4,
    border: '1px solid #aaa',
    boxShadow: '2px 2px 4px rgba(0, 0, 0, .2)',
    pointerEvents: 'auto',

    '&:empty': {
        minWidth: '4em',
    },

    '&:focus-visible': {
        outline: 'none',
    },
})

const NodeCommentEditor = ({node, ...props}) => {
    const map = useMapContext()
    const refEl = useRef()

    useEffect(
        () => {
            const el = refEl.current
            el.focus({preventScroll: true})
            const range = document.createRange()
            range.selectNodeContents(el)
            range.collapse(false)
            const selection = window.getSelection()
            selection.removeAllRanges()
            selection.addRange(range)
        },

        []
    )

    const finish = () => {
        map.execute(() => {
            node.comment = refEl.current.textContent
            map.selectNodes([node])
        })

        map.endEditNodeComment(node)
    }

    const onKeyDown = e => {
        // 避免与其他快捷键冲突
        e.stopPropagation()

        if (e.nativeEvent.isComposing) {
            return
        }

        // Enter/Esc 结束编辑
        if (! (e.ctrlKey || e.shiftKey)) {
            if (
                'Enter' === e.key ||
                'Escape' === e.key
            ) {
                e.preventDefault()
                finish()
            }
        }
    }

    return (
        <div
            ref={refEl}
            css={cssNodeCommentEditor}
            contentEditable
            suppressContentEditableWarning
            onBlur={finish}
            onDoubleClick={e => e.stopPropagation()}
            onKeyDown={onKeyDown}
            {...props}
        >
            {node.comment}
        </div>
    )
}

const cssNodeCommentTrigger = css({
    pointerEvents: 'auto',
    cursor: 'pointer',
})

const NodeCommentTrigger = ({node, ...props}) => {
    const map = useTreeDocContext()
    const onClick = () => map.startEditNodeComment(node)

    return (
        <FileTextOutlined
            css={cssNodeCommentTrigger}
            title={node.comment}
            onClick={onClick}
            {...props}
        />
    )
}

export default function NodeComment({node, ...props}) {
    const map = useMapContext()
    map.useNodeChange(node, ['comment'])
    const isEditing = map.useIsEditingComment(node)

    if (isEditing) {
        return (
            <NodeCommentEditor
                node={node}
                {...props}
            />
        )
    }
    else if (node.comment) {
        return (
            <NodeCommentTrigger
                node={node}
                {...props}
            />
        )
    }
    else {
        return null
    }
}
